import * as React from 'react';
import BPromise from 'bluebird';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { ActionItem } from 'Src/components/ActionsButton/ActionsButton';
import { ModalDialog } from 'Src/components/ModalDialog/ModalDialog';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { ColumnDefinition, getServerQueryFilter2, TABLE_SORT_DIRECTION } from 'Src/helpers/table/table';
import {
  getAllSuperAdminUserSessions,
  getSuperAdminUserSessions,
  getSuperAdminUserSessionsCount,
  deleteSuperAdminUserSession,
  SuperAdminUserSession
} from 'Src/helpers/service/superadmin/users';
import './SuperAdminUserSessions.css';

const INITIAL_SESSION_FILTERS = {
  key: '',
  role: '',
  'appDetails.appName': '',
  'appDetails.appVersion': '',
  'appDetails.appPlatform': [],
  createdAt: { from: '', to: '' },
  expireAt: { from: '', to: '' }
};

const SESSION_OPTIONS: any = {
  'appDetails.appPlatform': [
    { text: 'Web', value: 'WEB' },
    { text: 'iOS', value: 'IOS' },
    { text: 'Android', value: 'ANDROID' }
  ]
};

type DeleteMode = 'selected' | 'all';

function formatDateTime(value?: string): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatValue(value?: string): string {
  return value
    ? value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, letter => letter.toUpperCase())
    : '';
}

function shortenKey(key?: string): string {
  if (!key) {
    return '';
  }

  if (key.length <= 22) {
    return key;
  }

  return `${key.slice(0, 12)}...${key.slice(key.length - 8)}`;
}

function getSessionKey(session: SuperAdminUserSession): string {
  return shortenKey(session.key);
}

function getSessionRole(session: SuperAdminUserSession): string {
  return formatValue(session.role);
}

function getSessionAppName(session: SuperAdminUserSession): string {
  return session.appDetails && session.appDetails.appName ? session.appDetails.appName : '';
}

function getSessionAppVersion(session: SuperAdminUserSession): string {
  return session.appDetails && session.appDetails.appVersion ? session.appDetails.appVersion : '';
}

function getSessionAppPlatform(session: SuperAdminUserSession): string {
  return session.appDetails && session.appDetails.appPlatform ? formatValue(session.appDetails.appPlatform) : '';
}

function getSessionCreatedAt(session: SuperAdminUserSession): string {
  return formatDateTime(session.createdAt);
}

function getSessionExpireAt(session: SuperAdminUserSession): string {
  return formatDateTime(session.expireAt);
}

function getSessionLifeTime(session: SuperAdminUserSession): string {
  if (typeof session.lifeTime !== 'number') {
    return '';
  }

  const days = Math.floor(session.lifeTime / 86400);
  const hours = Math.floor((session.lifeTime % 86400) / 3600);

  if (days > 0 && hours > 0) {
    return `${days}d ${hours}h`;
  }

  if (days > 0) {
    return `${days}d`;
  }

  return `${Math.floor(session.lifeTime / 60)}m`;
}

function getSessionIsNotExtendable(session: SuperAdminUserSession): string {
  return session.isNotExtendable ? 'Yes' : 'No';
}

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Key',
    field: 'key',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: getSessionKey
  },
  {
    text: 'Role',
    field: 'role',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: getSessionRole
  },
  {
    text: 'App name',
    field: 'appDetails.appName',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: getSessionAppName
  },
  {
    text: 'Version',
    field: 'appDetails.appVersion',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: getSessionAppVersion
  },
  {
    text: 'Platform',
    field: 'appDetails.appPlatform',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getSessionAppPlatform
  },
  {
    text: 'Created',
    field: 'createdAt',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getSessionCreatedAt
  },
  {
    text: 'Expires',
    field: 'expireAt',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getSessionExpireAt
  },
  {
    text: 'Lifetime',
    field: 'lifeTime',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getSessionLifeTime
  },
  {
    text: 'Not extendable',
    field: 'isNotExtendable',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getSessionIsNotExtendable
  }
];

interface Props {
  userId: string;
}

interface State {
  items: SuperAdminUserSession[];
  selectedItems: SuperAdminUserSession[];
  currentPage: number;
  total: number;
  filters: any;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  isSelectAllChecked: boolean;
  isLoading: boolean;
  isDeleting: boolean;
  isDeleteModalOpen: boolean;
  deleteMode: DeleteMode;
  errorMessage: string;
  successMessage: string;
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
}

function parseDateFilterValue(value?: string): Date | undefined {
  if (!value) {
    return undefined;
  }

  const parts = value.split('-');

  if (parts.length !== 3) {
    return undefined;
  }

  const day = Number(parts[0]);
  const month = Number(parts[1]);
  const year = Number(parts[2]);

  if (!day || !month || !year) {
    return undefined;
  }

  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
}

function getDateIntervalWhere(value: any): any | undefined {
  if (!value || (!value.from && !value.to)) {
    return undefined;
  }

  const where: any = {};
  const from = parseDateFilterValue(value.from);
  const to = parseDateFilterValue(value.to);

  if (from) {
    where.$gte = from.toISOString();
  }

  if (to) {
    to.setUTCHours(23, 59, 59, 999);
    where.$lte = to.toISOString();
  }

  return Object.keys(where).length > 0 ? where : undefined;
}

function getSessionsWhere(filters: any): any {
  const where: any = {};

  if (filters.key) {
    where.key = { like: filters.key, options: 'i' };
  }

  if (filters.role) {
    where.role = { like: filters.role, options: 'i' };
  }

  if (filters['appDetails.appName']) {
    where['appDetails.appName'] = { like: filters['appDetails.appName'], options: 'i' };
  }

  if (filters['appDetails.appVersion']) {
    where['appDetails.appVersion'] = { like: filters['appDetails.appVersion'], options: 'i' };
  }

  if (Array.isArray(filters['appDetails.appPlatform']) && filters['appDetails.appPlatform'].length > 0) {
    where['appDetails.appPlatform'] = { $in: filters['appDetails.appPlatform'] };
  }

  const createdAt = getDateIntervalWhere(filters.createdAt);
  const expireAt = getDateIntervalWhere(filters.expireAt);

  if (createdAt) {
    where.createdAt = createdAt;
  }

  if (expireAt) {
    where.expireAt = expireAt;
  }

  return where;
}

function getUniqueSessions(sessions: SuperAdminUserSession[]): SuperAdminUserSession[] {
  const result: SuperAdminUserSession[] = [];

  (sessions || []).forEach(session => {
    if (!session || !session.key) {
      return;
    }

    const exists = result.some(item => item.key === session.key);

    if (!exists) {
      result.push(session);
    }
  });

  return result;
}

export class SuperAdminUserSessions extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      items: [],
      selectedItems: [],
      currentPage: FIRST_PAGE,
      total: 0,
      filters: { ...INITIAL_SESSION_FILTERS },
      isShowFilter: false,
      isDataFiltered: false,
      isSelectAllChecked: false,
      isLoading: false,
      isDeleting: false,
      isDeleteModalOpen: false,
      deleteMode: 'selected',
      errorMessage: '',
      successMessage: '',
      sortDirection: '',
      sortColumnsName: ''
    };
  }

  componentDidMount() {
    this.setItems(FIRST_PAGE);
  }

  setItems = (page: number): void => {
    const { userId } = this.props;
    const { filters, sortDirection, sortColumnsName } = this.state;
    const where = getSessionsWhere(filters);
    const order = sortDirection && sortColumnsName ? `${sortColumnsName} ${sortDirection}` : 'createdAt DESC';
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    this.setState({ isLoading: true, errorMessage: '' });

    BPromise.all([getSuperAdminUserSessionsCount(userId, where), getSuperAdminUserSessions(userId, serverQueryFilter)])
      .then(([countObj, items]) => {
        this.setState({
          items,
          total: countObj.count,
          currentPage: page,
          isLoading: false,
          errorMessage: '',
          isDataFiltered: Object.keys(where).length > 0,
          selectedItems: [],
          isSelectAllChecked: false
        });
      })
      .catch(error => {
        console.error('Can not load superadmin user sessions', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load sessions. Please check the session and try again.'
        });
      });
  };

  setCurrentPageParams = (currentPage: number): void => {
    this.setItems(currentPage);
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];
    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(column => column.field === filterField);
      const filterType = filter && filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const selectOptions = event.target.options;
        const value = [];

        for (let i = 0; i < selectOptions.length; i++) {
          if (selectOptions[i].selected) {
            value.push(selectOptions[i].value);
          }
        }

        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = (): void => {
    this.setItems(FIRST_PAGE);
  };

  onClearFilterClick = (): void => {
    this.setState({ filters: { ...INITIAL_SESSION_FILTERS } }, () => this.setItems(FIRST_PAGE));
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();
    this.setState({ isShowFilter: !this.state.isShowFilter });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName } = this.state;
    const nextSortDirection = sortColumnsName === sortField && sortDirection === 'ASC' ? 'DESC' : 'ASC';

    this.setState(
      {
        sortColumnsName: sortField,
        sortDirection: nextSortDirection as TABLE_SORT_DIRECTION
      },
      () => this.setItems(FIRST_PAGE)
    );
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    if (!selectedItem || !selectedItem.key) {
      return;
    }

    const selectedItemIndex = selectedItems.findIndex(item => item.key === selectedItem.key);

    if (selectedItemIndex === -1) {
      this.setState({ selectedItems: [...selectedItems, selectedItem], successMessage: '' });
    } else {
      const nextSelectedItems = selectedItems.slice();
      nextSelectedItems.splice(selectedItemIndex, 1);
      this.setState({ selectedItems: nextSelectedItems, successMessage: '' });
    }
  };

  onSelectAllClick = (): void => {
    const where = getSessionsWhere(this.state.filters);

    this.setState({ isLoading: true, errorMessage: '', successMessage: '' });

    getAllSuperAdminUserSessions(this.props.userId, where)
      .then(items => {
        this.setState({
          selectedItems: getUniqueSessions(items),
          isSelectAllChecked: true,
          isLoading: false
        });
      })
      .catch(error => {
        console.error('Can not select all superadmin user sessions', error);
        this.setState({ isLoading: false, errorMessage: 'Can not select all sessions.' });
      });
  };

  onSelectAllOnPageClick = (): void => {
    this.setState({
      selectedItems: getUniqueSessions(this.state.items),
      isSelectAllChecked: false,
      successMessage: ''
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({ selectedItems: [], isSelectAllChecked: false, successMessage: '' });
  };

  onDeleteSelectedSessionsClick = (): void => {
    if (this.state.selectedItems.length === 0) {
      return;
    }

    this.setState({ isDeleteModalOpen: true, deleteMode: 'selected', errorMessage: '', successMessage: '' });
  };

  onDeleteAllSessionsClick = (): void => {
    if (this.state.total === 0) {
      return;
    }

    this.setState({ isDeleteModalOpen: true, deleteMode: 'all', errorMessage: '', successMessage: '' });
  };

  onDeleteModalClose = (): void => {
    if (this.state.isDeleting) {
      return;
    }

    this.setState({ isDeleteModalOpen: false });
  };

  deleteSessions = (sessions: SuperAdminUserSession[]): Promise<void> => {
    const sessionsToDelete = getUniqueSessions(sessions).filter(session => Boolean(session.key));

    return BPromise.each(sessionsToDelete, session => deleteSuperAdminUserSession(this.props.userId, session.key)).then(
      () => undefined
    );
  };

  onDeleteSessionConfirm = (): void => {
    const { deleteMode, selectedItems } = this.state;

    if (deleteMode === 'selected' && selectedItems.length === 0) {
      this.setState({ isDeleteModalOpen: false, errorMessage: 'Please select sessions to delete.' });
      return;
    }

    this.setState({ isDeleting: true, errorMessage: '', successMessage: '' });

    const deletePromise: Promise<void> =
      deleteMode === 'all'
        ? getAllSuperAdminUserSessions(this.props.userId).then(sessions => this.deleteSessions(sessions))
        : this.deleteSessions(selectedItems);

    deletePromise
      .then(() => {
        const successMessage =
          deleteMode === 'all' ? 'All sessions have been deleted.' : 'Selected sessions have been deleted.';

        this.setState(
          {
            isDeleting: false,
            isDeleteModalOpen: false,
            selectedItems: [],
            isSelectAllChecked: false,
            successMessage
          },
          () => this.setItems(FIRST_PAGE)
        );
      })
      .catch(error => {
        console.error('Can not delete superadmin user sessions', error);
        this.setState({
          isDeleting: false,
          errorMessage: 'Can not delete sessions. Please check the selected sessions and try again.'
        });
      });
  };

  renderDeleteSummary(): React.ReactNode {
    const { deleteMode, selectedItems, total } = this.state;

    if (deleteMode === 'all') {
      return (
        <div className="SuperAdminUserSessionsDeleteSummary">
          <div>
            <strong>Sessions to delete:</strong> all sessions for this user
          </div>
          <div>
            <strong>Current count:</strong> {total}
          </div>
          <div className="text-danger mt-2">The user will be signed out from all devices and browsers.</div>
        </div>
      );
    }

    return (
      <div className="SuperAdminUserSessionsDeleteSummary">
        <div>
          <strong>Sessions to delete:</strong> {selectedItems.length}
        </div>
        {selectedItems.slice(0, 5).map(session => (
          <div key={session.key} className="SuperAdminUserSessionsDeleteItem">
            <div>
              <strong>Platform:</strong> {getSessionAppPlatform(session) || '—'}
              {' / '}
              <strong>Role:</strong> {getSessionRole(session) || '—'}
            </div>
            <div>
              <strong>App:</strong>{' '}
              {[getSessionAppName(session), getSessionAppVersion(session)].filter(Boolean).join(' / ') || '—'}
            </div>
            <div>
              <strong>Created:</strong> {getSessionCreatedAt(session) || '—'}
              {' / '}
              <strong>Expires:</strong> {getSessionExpireAt(session) || '—'}
            </div>
            <div>
              <strong>Session key:</strong> <code>{session.key}</code>
            </div>
          </div>
        ))}
        {selectedItems.length > 5 && <div className="text-muted">And {selectedItems.length - 5} more session(s).</div>}
      </div>
    );
  }

  render() {
    const {
      items,
      selectedItems,
      currentPage,
      total,
      filters,
      isShowFilter,
      isDataFiltered,
      isSelectAllChecked,
      isLoading,
      isDeleting,
      isDeleteModalOpen,
      deleteMode,
      errorMessage,
      successMessage,
      sortDirection,
      sortColumnsName
    } = this.state;

    const actionItems: ActionItem[] = [
      {
        itemText: selectedItems.length > 1 ? 'Delete selected sessions' : 'Delete selected session',
        isActive: selectedItems.length > 0,
        onItemClick: this.onDeleteSelectedSessionsClick
      },
      {
        itemText: 'Delete all sessions',
        isActive: total > 0,
        onItemClick: this.onDeleteAllSessionsClick
      }
    ];

    return (
      <div className="SuperAdminUserSessions">
        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {successMessage && !isLoading && <div className="alert alert-success">{successMessage}</div>}
        {(isLoading || isDeleting) && <Loader />}

        <div className="alert alert-info SuperAdminUserSessionsInfo">
          Deleting a session signs the user out from that login. Use <strong>Delete all sessions</strong> when the user
          needs to be signed out everywhere.
        </div>

        {!isLoading && (
          <Grid2
            dataItems={items}
            filters={filters}
            options={SESSION_OPTIONS}
            currentPage={currentPage}
            total={total}
            isSelectAllChecked={isSelectAllChecked}
            isDataFiltered={isDataFiltered}
            sortDirection={sortDirection}
            sortColumnsName={sortColumnsName}
            isShowFilter={isShowFilter}
            dataItemsSelected={selectedItems}
            columns={COLUMNS}
            actionItems={actionItems}
            onItemClick={this.onItemClick}
            onSortClick={this.onTableSortClick}
            onApplyFilterClick={this.onApplyFilterClick}
            onClearFilterClick={this.onClearFilterClick}
            onTableFilterChange={this.onTableFilterChange}
            onTableFilterClick={this.onTableFilterClick}
            setCurrentPageParams={this.setCurrentPageParams}
            onSelectAllClick={this.onSelectAllClick}
            onSelectAllOnPageClick={this.onSelectAllOnPageClick}
            onUnselectAllClick={this.onUnselectAllClick}
          />
        )}

        <ModalDialog
          isOpen={isDeleteModalOpen}
          title={deleteMode === 'all' ? 'Delete all sessions' : 'Delete selected sessions'}
          positiveButtonTitle={isDeleting ? 'Deleting...' : 'Delete'}
          negativeButtonTitle="Cancel"
          onPositiveButtonClick={this.onDeleteSessionConfirm}
          onNegativeButtonClick={this.onDeleteModalClose}
        >
          <p>
            Are you sure you want to delete {deleteMode === 'all' ? 'all sessions for this user' : 'selected sessions'}?
          </p>
          {this.renderDeleteSummary()}
        </ModalDialog>
      </div>
    );
  }
}

